import Router from "next/router";
import { memo, useCallback, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import clip from "text-clipper";

import { bookEventById } from "~/api/events/bookEventById";
import Img from "~/components/common/img";
import MainButton from "~/components/common/mainButton";
import Spacer from "~/components/common/spacer";
import Modal from "~/components/ui/modal";
import { useAuth } from "~/contexts/providers/auth";
import { useEvent } from "~/contexts/providers/events";
import { useLanguage } from "~/contexts/providers/language";
import englishTranslations from "~/localization/en";
import frenchTranslations from "~/localization/fr";
import { EventType, GuestType } from "~/types/event/event.types";
import { BookingStatus, EventTypeEnum } from "~/types/event/eventEnums.type";
import { locationToColors, locationToNames } from "~/utils/constants/locations";
import { formatDate, formatTime } from "~/utils/functions/date.functions";
import { getLanguage } from "~/utils/functions/language.function";

import GuestForm from "../guestForm";
import styles from "./index.module.scss";

type Props = {
  event: EventType;
  setCannotBookPopup: (value: boolean) => void;
};

const HighlightedEvent = ({ event, setCannotBookPopup }: Props) => {
  const [readMoreClicked, setReadMoreClicked] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const { user } = useAuth();
  const { language } = useLanguage();
  const { setMyEvents, myEvents } = useEvent();
  const queryClient = useQueryClient();

  const accountTexts =
    language === "En"
      ? englishTranslations.accountPage
      : frenchTranslations.accountPage;

  const waitlist =
    language === "En"
      ? englishTranslations.accountPage.waitlist
      : frenchTranslations.accountPage.waitlist;
  const attending =
    language === "En"
      ? englishTranslations.accountPage.attending
      : frenchTranslations.accountPage.attending;
  const close =
    language === "En"
      ? englishTranslations.accountPage.close
      : frenchTranslations.accountPage.close;
  const readMore =
    language === "En"
      ? englishTranslations.accountPage.readMore
      : frenchTranslations.accountPage.readMore;

  const { mutate: BookEventById, isLoading } = useMutation(bookEventById, {
    onSuccess: (res) => {
      queryClient.invalidateQueries("events");
      setMyEvents([...myEvents, { ...event, booking: res }]);
    },
  });

  const canBook = useMemo(
    () =>
      myEvents.filter(({ dateFrom }) => new Date(dateFrom) > new Date())
        .length < 5,
    [myEvents]
  );

  const handleSwitchModalState = (status: boolean) => {
    setModalOpen(status);
  };

  const [eventDate, timeFrom, timeTo] = useMemo(
    () => [
      formatDate(event.dateFrom, language),
      formatTime(event.dateFrom),
      formatTime(event.dateTo),
    ],
    [event.dateFrom, event.dateTo, language]
  );
  const allowedGuests =
    user?.subscriptionType === "MEMBER_PLUS"
      ? event.allowedGuestsPerParticipant * 2
      : event.allowedGuestsPerParticipant;
  const bookingGuests = event.booking?.guests.length ?? 0;
  const guestNumber = event.booking ? bookingGuests : allowedGuests;

  const handleReadMoreClick = () => {
    setReadMoreClicked(!readMoreClicked);
  };

  const handleBookEvent = (guests: GuestType[]) => {
    BookEventById({ eventId: event.id, guests });
    handleSwitchModalState(false);
  };

  const handleBookClick = useCallback(() => {
    if (event.type === EventTypeEnum.NORMAL) {
      if (!canBook) {
        setCannotBookPopup(true);
        console.log("you can't book");
        return;
      }
      handleSwitchModalState(true);
    } else if (event.bookingLink) Router.push(event.bookingLink);
  }, [canBook, event.bookingLink, event.type, handleSwitchModalState]);

  return (
    <div className={styles.container}>
      <div className={styles.eventInfoContainer}>
        <div className={styles.eventInfo}>
          <div className={styles.eventInfoLine + " " + styles.reverse}>
            <div className={styles.eventName}>
              <MainButton>{event.title}</MainButton>
            </div>
            <div className={styles.eventLocationTags}>
              <div
                className={styles.eventLocation}
                style={{ color: `${locationToColors[event.location]}` }}
              >
                {locationToNames[event.location]}
              </div>
              <div className={styles.eventTags}>
                {event.tags
                  .map((tag) => getLanguage(tag, "name", language))
                  .join(", ")}
              </div>
            </div>
          </div>

          <span className={styles.spacer}>
            <Spacer height="10px" width="100%" />
          </span>

          <div className={styles.eventInfoLine}>
            <div className={styles.dateTime}>
              <div className={styles.date}>{eventDate}</div>
              <div className={styles.time}>
                {timeFrom} → {timeTo}
              </div>
            </div>
            <div className={styles.bookGuests}>
              {!event.booking ? (
                <MainButton
                  theme={"white"}
                  onClick={handleBookClick}
                  isLoading={isLoading}
                >
                  {event.isFull ? accountTexts.waitingList : accountTexts.book}
                </MainButton>
              ) : (
                <MainButton
                  theme={
                    event.booking.status === BookingStatus.MAIN
                      ? "green"
                      : "yellow"
                  }
                  isLoading={isLoading}
                  uiOnly
                >
                  {event.booking.status === BookingStatus.MAIN
                    ? attending
                    : waitlist}
                </MainButton>
              )}
              {(canBook || event.booking?.status) && (
                <div className={styles.guestsButtonContainer}>
                  {event.booking
                    ? guestNumber > 1
                      ? accountTexts.invitedGuests
                      : accountTexts.invitedGuest
                    : `${accountTexts.invitation}${guestNumber > 1 ? "s" : ""}`}
                  <MainButton theme="transparent-grey" isLoading={isLoading}>
                    {guestNumber}
                  </MainButton>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className={styles.eventInfoDescription}>
          {!readMoreClicked
            ? clip(event.descriptionEn, 300)
            : event.descriptionEn}
          <br />
          <button onClick={handleReadMoreClick} className={styles.readMore}>
            {readMoreClicked ? close : readMore}
          </button>
        </div>
      </div>

      <Img
        src={event.highlightPicturePath ?? event.picturePath}
        alt={event.title}
      />
      <div className={styles.eventNameMobile}>
        <MainButton uiOnly>{event.title}</MainButton>
      </div>

      <Modal
        open={modalOpen}
        onClose={() => handleSwitchModalState(false)}
        showAnimation="fade-in"
      >
        <GuestForm
          type="book"
          eventName={event.title}
          currentGuests={0}
          allowedGuests={guestNumber}
          handleSubmitForm={handleBookEvent}
          isLoading={isLoading}
        />
      </Modal>
    </div>
  );
};

export default memo(HighlightedEvent);
